import {
  Box,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { ResourceType } from '@repo/api-gw-sdk';
import { useEffect, useMemo } from 'react';

import {
  PREMIUM_STORAGE_TYPES,
  VirtualMachineSizeSelect,
} from '@/components/selectors/virtualMachineSizeSelect';
import { useDAL } from '@/contexts/useDAL';

import type { AzureDiskConfigState } from '../restoreAzureVMWizard';

interface VirtualMachineSizeSectionProps {
  accountId?: string;
  location?: string;
  originalVMSize?: string;
  vmSize?: string;
  keepOriginalVMSize: boolean;
  disksState: AzureDiskConfigState[];
  onVmSizeChange: (vmSize: string) => void;
  onKeepOriginalVMSizeChange: (keepOriginal: boolean) => void;
  onCompatibilityChange: (compatible: boolean | undefined) => void;
}

export const VirtualMachineSizeSection = ({
  accountId,
  location,
  originalVMSize,
  vmSize,
  keepOriginalVMSize,
  disksState,
  onVmSizeChange,
  onKeepOriginalVMSizeChange,
  onCompatibilityChange,
}: VirtualMachineSizeSectionProps) => {
  const dal = useDAL();

  const { data: skuData } = dal.restore.azureRestoreConfig.useListSKUs(
    accountId ?? '',
    location ?? '',
    ResourceType.AzureVirtualMachine
  );

  const isOriginalVmCompatible = useMemo(() => {
    if (!skuData?.skus) return undefined;

    const originalSKU = skuData.skus.find((s) => s.name === originalVMSize);
    if (!originalSKU) return false;

    const requiresPremiumStorage =
      !disksState ||
      disksState.length === 0 ||
      disksState.some((d) => {
        const diskType = d.diskSettings.settings.type;
        if (!diskType) return true;
        return PREMIUM_STORAGE_TYPES.some((pt) => diskType.includes(pt));
      });

    if (requiresPremiumStorage) {
      const supportsPremium =
        originalSKU.capabilities?.PremiumIO?.toLowerCase() === 'true';
      if (!supportsPremium) return false;
    }

    const dataDisksCount = (disksState?.length ?? 1) - 1;
    const maxDataDisks = Number(
      originalSKU.capabilities?.MaxDataDiskCount ?? 0
    );
    if (maxDataDisks < dataDisksCount) return false;

    return true;
  }, [skuData?.skus, originalVMSize, disksState]);

  useEffect(() => {
    onCompatibilityChange(isOriginalVmCompatible);
  }, [isOriginalVmCompatible, onCompatibilityChange]);

  const showOriginalSizeTable =
    isOriginalVmCompatible === true && keepOriginalVMSize;
  const showSizePicker =
    isOriginalVmCompatible !== undefined && !showOriginalSizeTable;

  return (
    <>
      {isOriginalVmCompatible === true && (
        <Box className='flex justify-between items-center mt-[20px]'>
          <Typography className='font-semibold' variant='body1'>
            Keep original size
          </Typography>
          <Switch
            size='small'
            checked={keepOriginalVMSize}
            onChange={() => onKeepOriginalVMSizeChange(!keepOriginalVMSize)}
          />
        </Box>
      )}
      {showOriginalSizeTable && (
        <Box className='mt-[10px] w-1/2'>
          <Table size='small'>
            <TableBody>
              <TableRow>
                <TableCell className='border-0 px-0'>
                  Virtual machine size
                </TableCell>
                <TableCell className='border-0 px-0'>
                  {originalVMSize}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
      {showSizePicker && (
        <Stack direction='row' gap='24px' className='mt-[10px] mb-[24px]'>
          <VirtualMachineSizeSelect
            skus={skuData?.skus}
            vmSize={vmSize}
            disksState={disksState}
            originalVMSize={originalVMSize}
            onChange={onVmSizeChange}
            autoSuggest={!isOriginalVmCompatible}
          />
          <Box className='flex-1' />
        </Stack>
      )}
    </>
  );
};
